/**
 * Helper functions to map filter numeric IDs to API string values
 */

import { AmenityApiMapping, PropertyTypeApiMapping, ProvisionApiMapping } from '@app/enums';
import { ProvisionType } from '@app/models/api/api-types';
import { MapAccommodationsFilters } from '@app/models/map-accommodations.model';

/**
 * Mapping from ProvisionApiMapping numeric IDs to ProvisionType enum values
 */
export const PROVISION_API_TO_TYPE_MAPPING: Record<number, ProvisionType> = {
    [ProvisionApiMapping.Breakfast]: ProvisionType.Breakfast,
    [ProvisionApiMapping.HalfBoard]: ProvisionType.HalfBoard,
    [ProvisionApiMapping.AllInclusive]: ProvisionType.AllInclusive,
    [ProvisionApiMapping.FullBoard]: ProvisionType.FullBoard,
    [ProvisionApiMapping.NoMeal]: ProvisionType.NoMeal,
};

export const mapFilterValues = <TInput extends string | number | symbol, TOutput>(
    inputIds: Array<TInput>,
    apiMap: Record<TInput, TOutput>,
): Array<TOutput> => {
    return inputIds.map(id => apiMap[id]).filter(Boolean);
};

/**
 * Maps provision IDs from ProvisionApiMapping to ProvisionType enum values
 * @param provisionIds Array of provision IDs (numeric)
 * @returns Array of ProvisionType enum values
 */
export const mapProvisionToTypes = (
    provisionIds?: (number | undefined)[],
): ProvisionType[] | undefined => {
    if (!provisionIds) return undefined;

    const filtered = provisionIds.filter((id): id is number => id !== undefined);
    return filtered.length > 0
        ? mapFilterValues(filtered, PROVISION_API_TO_TYPE_MAPPING)
        : undefined;
};

export const convertFiltersToApiParams = (filters: MapAccommodationsFilters) => {
    const { propertyTypeIds = [], amenityIds = [], provisionIds = [] } = filters;

    return {
        propertyType: mapFilterValues<number, string>(propertyTypeIds, PropertyTypeApiMapping),
        amenities: mapFilterValues<number, string>(amenityIds, AmenityApiMapping),
        provision: mapFilterValues<number, string>(provisionIds, ProvisionApiMapping),
    };
};
